<template>
    <div>
        <md-progress-spinner v-if="isLoading"
            md-mode="indeterminate"></md-progress-spinner>

        <md-card v-else
            class="md-layout-item md-size-100 md-small-size-100">
            <md-card-content>
                <div class="md-layout md-gutter">
                    <div class="md-layout-item md-size-75">
                        <md-field md-clearable
                            :class="{'md-invalid': $v.post.title.$dirty && $v.post.title.$invalid}">
                            <label>{{ $t('general.title') }}</label>
                            <md-input v-model="post.title"
                                @input="$v.post.title.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.post.title.$dirty && $v.post.title.$invalid">
                                {{ $tc('validations.required-field', 1) }}
                            </span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.post.description.$dirty && $v.post.description.$invalid}">
                            <label>{{ $t('general.description') }}</label>
                            <md-input v-model="post.description"
                                @input="$v.post.description.$touch()"></md-input>
                            <span class="md-error"
                                v-show="$v.post.description.$dirty && $v.post.description.$invalid">
                                {{ $tc('validations.required-field', 1) }}
                            </span>
                        </md-field>

                        <md-field md-clearable
                            :class="{'md-invalid': $v.post.promotion_link.$invalid}">
                            <label>{{ $t('posts.link') }}</label>
                            <md-input v-model="post.promotion_link"></md-input>
                            <span class="md-error"
                                v-show="$v.post.promotion_link.$invalid">
                                {{ $t('validations.invalid-format') }}
                            </span>
                        </md-field>

                        <flat-pickr v-model="post.expire_date"
                            :config="dateConfig"
                            class="form-control"
                            :placeholder="$t('posts.expiration-date')"
                            name="date">
                        </flat-pickr>
                    </div>
                    <div class="md-layout-item md-size-25"
                        id="previewPicture">
                        <label class="labelTitle">{{ $t('posts.image') }}</label>
                        <template>
                            <div class="base-image-input"
                                :style="{ 'background-size':'contain','background-image': `url(${pictureData})` }"
                                @click="chooseImage">
                                <span v-if="!pictureData"
                                    class="placeholder">{{ $t('general.select-image') }}</span>
                                <input class="file-input"
                                    ref="fileInput"
                                    type="file"
                                    @input="onSelectFile"
                                    accept="image/*">
                            </div>
                        </template>
                        <span class="error-msg"
                            v-show="$v.pictureData.$invalid">{{ $tc('validations.required-field',1) }}</span>
                    </div>
                    <div class="md-layout-item md-size-100">
                        <div class="action-bar">
                            <md-button class="md-raised"
                                @click="close">
                                {{ $t('actions.cancel') }}
                            </md-button>
                            <md-button class="md-raised md-primary"
                                @click="savePost"
                                :disabled="$v.$invalid">
                                {{ $t('actions.confirm') }}
                            </md-button>
                        </div>
                    </div>
                </div>
            </md-card-content>
        </md-card>
    </div>
</template>

<script>
import _ from 'lodash'
import postsService from '@/components/posts/services/'
import picturesService from '@/components/pictures/services/'
import { required, url } from 'vuelidate/lib/validators'
import axios from 'axios'
import { Portuguese } from 'flatpickr/dist/l10n/pt.js'

const loadingKeys = {
    creatingItem: 'creating-item',
    fetchingItem: 'fetching-item'
}

export default {
    name: 'PostsMaintenance',

    data () {
        return {
            post: {
                post_id: null,
                title: '',
                description: '',
                picture_id: null,
                created_at: null,
                updated_at: null,
                deleted_at: null,
                expire_date: null,
                picture_url: null,
                picture_name: '',
                promotion_link: null,
                user_id: null,
                active: null
            },

            dateConfig: {
                altFormat: 'F j, Y',
                enableTime: true,
                noCalendar: false,
                dateFormat: 'Y-m-d H:i',
                locale: Portuguese,
                minDate: new Date()
            },

            isViewMode: false,
            isAddMode: false,
            isEditMode: false,

            loadings: [],

            pictureData: ''
        }
    },

    validations: {
        post: {
            title: {
                required
            },
            description: {
                required
            },
            promotion_link: {
                url
            },
            expire_date: {
                required
            }
        },
        pictureData: {
            required
        }
    },

    computed: {
        isLoading () {
            return this.isFetchingPosts || this.isCreatingPosts
        },

        isCreatingPosts () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.creatingItem
            ).length
        },

        isFetchingPosts () {
            return !!this.loadings.filter(
                loading => loading.key === loadingKeys.fetchingItem
            ).length
        }
    },

    methods: {
        setMode (mode) {
            switch (mode) {
                case 'view-post':
                    this.isViewMode = true
                    break
                case 'add-post':
                    this.isAddMode = true
                    break
                case 'edit-post':
                    this.isEditMode = true
                    break
            }
        },

        async savePost () {
            _.fill(this.loadings, loadingKeys.creatingItem)

            let self = this
            let params = {
                post_id: self.post.post_id,
                description: self.post.description,
                title: self.post.title,
                picture_id: self.post.picture_id,
                promotion_link: self.post.promotion_link,
                expire_date: new Date(self.post.expire_date),
                user_id: this.$store.getters.user.userId,
                active: this.isAddMode ? 1 : self.post.active
            }
            let pictureParams = {
                picture_id: self.post.picture_id,
                name: self.post.title,
                data: _.split(self.pictureData, 'base64,')[1],
                active: this.isAddMode ? 1 : self.post.active
            }
            let response

            if (this.isAddMode) {
                response = await picturesService.create(pictureParams)
                if (response.data.picture_id != null) {
                    params.picture_id = response.data.picture_id
                } else {
                    return
                }
                response = await postsService.create(params)
            } else {
                response = await picturesService.update(pictureParams)
                response = await postsService.update(params)
            }

            _.pull(this.loadings, loadingKeys.creatingItem)
            this.$router.go(-1)
        },

        async fetchPost () {
            _.fill(this.loadings, loadingKeys.fetchingItem)

            let self = this
            let id = self.$route.params.id
            let parameters = {
                filter: {
                    post_id: id
                }
            }

            let response = await postsService.retrieve(parameters)
            self.post = _.cloneDeep(response.data.results[0])
            self.pictureData = await this.getBase64(self.post.picture_url)
            self.post = _.cloneDeep(self.post)

            _.pull(this.loadings, loadingKeys.fetchingItem)
        },

        close () {
            this.$router.go(-1)
        },

        async getBase64 (url) {
            return axios
                .get(url, {
                    responseType: 'arraybuffer',
                    Authorization: 'Bearer ' + localStorage.getItem('token')
                })
                .then(response => {
                    let image = btoa(
                        new Uint8Array(response.data).reduce(
                            (data, byte) => data + String.fromCharCode(byte),
                            ''
                        )
                    )

                    return `data:${response.headers['content-type'].toLowerCase()};base64,${image}`
                })
        },

        chooseImage () {
            this.$refs.fileInput.click()
        },

        onSelectFile () {
            const input = this.$refs.fileInput
            const files = input.files
            if (files && files[0]) {
                const reader = new FileReader()
                reader.onload = e => {
                    this.pictureData = e.target.result
                }
                reader.readAsDataURL(files[0])
                this.$emit('input', files[0])
            }
        }
    },

    created () {
        this.setMode(this.$route.name)

        if (!this.isAddMode) {
            this.fetchPost()
        }
    }
}
</script>
